/**
 * Virtual Assistant Response Generation Logic
 * Pattern matching and context-aware responses for dashboard questions
 */

import { getFinancialExplanation, detectFinancialConcept } from './financialKnowledge';
import { getFinancialKnowledge, getInterpretation } from './financialKnowledgeTranslations';
import i18n from '../i18n/config';

const tr = (key, defaultValue, options = {}) => i18n.t(`assistantResponses.${key}`, { defaultValue, ...options });

/**
 * Generate assistant response based on user question and dashboard data
 * @param {string} question - User's question
 * @param {object} dashboardData - Current dashboard state
 * @returns {string} - Assistant's response
 */
export function generateAssistantResponse(question, dashboardData) {
    const q = question.toLowerCase().trim();
    
    // Extract dashboard data with defaults
    const {
        risk = 'Averse',
        currentAllocation = [],
        exposureTimelineData = { timeline: [], current: null },
        correlationData = { m: "0" },
        performanceData = {},
        volatilityData = {},
        relativePerformanceData = {},
        marketStatus = { isOpen: false, statusLabel: 'Unknown' },
        quotes = [],
        marketContext = null
    } = dashboardData;

    const riskLabel = i18n.t(`dashboard.riskProfiles.labels.${risk}`, risk);

    // ============================================================
    // ALTRADENATIVE PLATFORM QUESTIONS (Check First)
    // ============================================================
    
    // What is Altradenative?
    if (q.includes('what is altradenative') || q.includes('what\'s altradenative') || 
        (q.includes('altradenative') && (q.includes('what') || q.includes('explain')))) {
        return tr('whatIsAltradenative', 'Altradenative is an online dashboard that makes investing in stocks simpler and more efficient, tailored to each user\'s risk profile.\n\nOur algorithm constantly monitors the U.S. market and builds a portfolio using only three types of assets:\n\n- A broad market index (S&P500 or Nasdaq100)\n- A leveraged version of that index (S&P500x3 or Nasdaq100x3)\n- A risk‑free asset (USD cash or U.S. Treasury Bonds)\n\nIt\'s important to note that Altradenative is an interactive virtual analyst: it provides information and education for investment decisions based on quantitative methods and statistical analysis. We do not recommend specific purchases, take orders, or manage investor funds.');
    }

    // Past Performance / Backtesting
    if (q.includes('past performance') || q.includes('backtest') || q.includes('backtesting') || 
        q.includes('historical performance') || q.includes('20 years') || q.includes('tested')) {
        return tr('pastPerformance', 'This model has been tested over 20 years:\n\n- For neutral or aggressive profiles, it has outperformed the S&P500\'s annual returns in more than 80% of cases, while keeping losses similar during major downturns.\n\n- For conservative profiles, it has reduced losses by over 50% in bear markets, while still capturing strong gains in bull markets.\n\n**Important:** Investing in stocks carries risk of loss, sometimes significant. For neutral and aggressive profiles, Altradenative\'s algorithm may also generate losses greater than those of global indices.');
    }

    // How does it work?
    if (q.includes('how does it work') || q.includes('how does altradenative work') || 
        q.includes('how it works') || (q.includes('how') && q.includes('work') && q.includes('algorithm'))) {
        return tr('howItWorks', 'Altradenative\'s algorithm constantly monitors the U.S. market and builds a portfolio using only three types of assets:\n\n- **Broad market index**: S&P500 or Nasdaq100 (represented by ETFs like SPY, VOO, or QQQ)\n- **Leveraged version**: A 3x leveraged version of that index (S&P500x3 like UPRO, or Nasdaq100x3 like TQQQ)\n- **Risk-free asset**: USD cash or U.S. Treasury Bonds\n\nThe algorithm dynamically adjusts the allocation between these three asset types based on market conditions, volatility, and your selected risk profile. This happens through daily rebalancing to optimize risk-adjusted returns.');
    }

    // Three asset types / asset types
    if (q.includes('three asset') || q.includes('asset types') || q.includes('what assets') || 
        (q.includes('assets') && (q.includes('use') || q.includes('types')))) {
        return tr('howItWorks', 'Altradenative\'s algorithm constantly monitors the U.S. market and builds a portfolio using only three types of assets:\n\n- **Broad market index**: S&P500 or Nasdaq100 (represented by ETFs like SPY, VOO, or QQQ)\n- **Leveraged version**: A 3x leveraged version of that index (S&P500x3 like UPRO, or Nasdaq100x3 like TQQQ)\n- **Risk-free asset**: USD cash or U.S. Treasury Bonds\n\nThe algorithm dynamically adjusts the allocation between these three asset types based on market conditions, volatility, and your selected risk profile. This happens through daily rebalancing to optimize risk-adjusted returns.');
    }

    // Virtual analyst / advisor clarification
    if (q.includes('advisor') || q.includes('adviser') || q.includes('virtual analyst') || 
        q.includes('does altradenative') && (q.includes('recommend') || q.includes('manage') || q.includes('trade'))) {
        return tr('whatIsAltradenative', 'Altradenative is an online dashboard that makes investing in stocks simpler and more efficient, tailored to each user\'s risk profile.\n\nOur algorithm constantly monitors the U.S. market and builds a portfolio using only three types of assets:\n\n- A broad market index (S&P500 or Nasdaq100)\n- A leveraged version of that index (S&P500x3 or Nasdaq100x3)\n- A risk‑free asset (USD cash or U.S. Treasury Bonds)\n\nIt\'s important to note that Altradenative is an interactive virtual analyst: it provides information and education for investment decisions based on quantitative methods and statistical analysis. We do not recommend specific purchases, take orders, or manage investor funds.');
    }

    // ============================================================
    // FINANCIAL CONCEPT EXPLANATIONS (Check After Platform Questions)
    // ============================================================
    
    // Check if question is asking about a financial concept
    const concept = detectFinancialConcept(q);
    if (concept) {
        return explainFinancialConcept(concept, q, dashboardData);
    }

    // General financial concept questions (what is, explain, define, how does)
    if (q.match(/\b(what is|what's|explain|define|how does|tell me about)\b/)) {
        const conceptMatch = detectFinancialConcept(q);
        if (conceptMatch) {
            return explainFinancialConcept(conceptMatch, q, dashboardData);
        }
    }

    // Dashboard Questions - Allocation
    if (q.includes('allocation') || q.includes('portfolio') || q.includes('position')) {
        const spyPercent = currentAllocation.find(a => a.ticker === 'SPY' || a.ticker === 'SPY or VOO')?.percent || 0;
        const tqqqPercent = currentAllocation.find(a => a.ticker === 'TQQQ')?.percent || 0;
        const cashPercent = currentAllocation.find(a => a.ticker === 'Cash' || a.ticker === 'T-bill or cash')?.percent || 0;
        
        const currentExposure = exposureTimelineData?.current?.fullExposure || 0;
        return tr('allocation', `This portfolio setup shows an allocation of **${spyPercent}%** in S&P 500 correlated ETFs (SPY), **${tqqqPercent}%** in leveraged ETFs (TQQQ), and **${cashPercent}%** in risk-free assets. Current total exposure to equity markets is **${currentExposure.toFixed(1)}%**. This allocation reflects the ${riskLabel} risk profile and adapts dynamically to market conditions.`, {
            spyPercent,
            tqqqPercent,
            cashPercent,
            currentExposure: currentExposure.toFixed(1),
            risk: riskLabel
        });
    }

    // Dashboard Questions - Leverage
    if (q.includes('leverage') || q.includes('tqqq')) {
        const leveragePercent = currentAllocation.find(a => a.ticker === 'TQQQ')?.percent || 0;
        const currentExposure = exposureTimelineData?.current?.fullExposure || 0;
        if (leveragePercent > 0) {
            return tr('leverage.active', `This portfolio setup currently shows **${leveragePercent}%** leverage through TQQQ. This leveraged position amplifies returns when the market moves favorably but also increases risk during downturns. The algorithm adjusts leverage based on market conditions and the ${riskLabel} risk profile. Total equity exposure is **${currentExposure.toFixed(1)}%**.`, {
                leveragePercent,
                risk: riskLabel,
                currentExposure: currentExposure.toFixed(1)
            });
        } else {
            return tr('leverage.none', `This portfolio setup currently shows **0%** leverage. The algorithm has reduced leverage based on current market conditions and risk assessment. This is consistent with the ${riskLabel} risk profile. Total equity exposure is **${currentExposure.toFixed(1)}%**.`, {
                risk: riskLabel,
                currentExposure: currentExposure.toFixed(1)
            });
        }
    }

    // Dashboard Questions - Exposure (general or timeline)
    if (q.includes('exposure')) {
        const currentExposure = exposureTimelineData?.current?.fullExposure || 0;
        const timeline = exposureTimelineData?.timeline || [];
        let trend = 'stable';
        if (timeline.length >= 22) {
            const now = timeline[timeline.length - 1]?.fullExposure || 0;
            const oneMonthAgo = timeline[timeline.length - 22]?.fullExposure || 0;
            if (now > oneMonthAgo + 2) trend = 'increasing';
            else if (now < oneMonthAgo - 2) trend = 'decreasing';
        }
        const trendText = tr(`exposure.trend.${trend}`, trend);
        
        if (q.includes('time') || q.includes('timeline') || q.includes('over time')) {
            return tr('exposure.timeline', `Current Full Exposure is **${currentExposure.toFixed(1)}%**, representing the total portfolio allocation to equity markets (SPY correlated + Leveraged ETF). The exposure trend is **${trendText}**. The Exposure in Time chart shows how this portfolio setup has adjusted exposure over the last 3 years based on market conditions.`, {
                currentExposure: currentExposure.toFixed(1),
                trend: trendText
            });
        } else {
            return tr('exposure.general', `Current Full Exposure is **${currentExposure.toFixed(1)}%**, representing the total portfolio allocation to equity markets (SPY correlated + Leveraged ETF). The exposure trend is **${trendText}**. You can view the "Exposure in Time" chart to see how exposure has evolved over the last 3 years.`, {
                currentExposure: currentExposure.toFixed(1),
                trend: trendText
            });
        }
    }

    // Dashboard Questions - Beta (Enhanced with financial knowledge)
    if (q.includes('beta') && !q.includes('what is') && !q.includes('explain')) {
        const beta = parseFloat(correlationData.m || "0");
        const knowledge = getFinancialKnowledge('beta');
        const interpretation = getInterpretation('beta', beta);
        
        if (knowledge) {
            return `${knowledge.what}\n\nThis portfolio setup's beta is **${beta.toFixed(2)}**. ${interpretation}\n\n${knowledge.relevance}`;
        }
    }

    // Dashboard Questions - Performance
    if (q.includes('performance') || q.includes('return') || q.includes('how has')) {
        const perf3y = relativePerformanceData[risk]?.['Last 3 Years'] || { Native: 0, SPY: 0 };
        const perf1y = relativePerformanceData[risk]?.['Last Year'] || { Native: 0, SPY: 0 };
        const perf3m = relativePerformanceData[risk]?.['Last 3 Months'] || { Native: 0, SPY: 0 };
        
        const comparison = perf3y.Native > perf3y.SPY ? 'higher' : perf3y.Native < perf3y.SPY ? 'lower' : 'similar';
        const comparisonTextKey = `performanceComparison.${comparison}`;
        const comparisonText = tr(comparisonTextKey, comparison === 'higher' ? 'higher returns than' : comparison === 'lower' ? 'lower returns than' : 'similar returns to');
        return tr('performance', `Performance data for the ${risk} profile: **Last 3 Months**: ${perf3m.Native}% (vs SPY ${perf3m.SPY}%), **Last Year**: ${perf1y.Native}% (vs SPY ${perf1y.SPY}%), **Last 3 Years**: ${perf3y.Native}% (vs SPY ${perf3y.SPY}%). This portfolio setup shows ${comparisonText} the market over the long term.`, {
            risk,
            perf3mNative: perf3m.Native,
            perf3mSpy: perf3m.SPY,
            perf1yNative: perf1y.Native,
            perf1ySpy: perf1y.SPY,
            perf3yNative: perf3y.Native,
            perf3ySpy: perf3y.SPY,
            comparisonText
        });
    }

    // Dashboard Questions - Volatility (Enhanced with financial knowledge)
    if ((q.includes('volatility') || (q.includes('risk') && !q.includes('profile'))) && !q.includes('what is') && !q.includes('explain')) {
        const vol3y = volatilityData[risk]?.['Last 3 Years'] || { Native: 0, SPY: 0 };
        const vol1y = volatilityData[risk]?.['Last Year'] || { Native: 0, SPY: 0 };
        const knowledge = getFinancialKnowledge('volatility');
        const interpretation = getInterpretation('volatility', vol3y.Native);
        
        if (knowledge) {
            return tr('volatility', `${knowledge.what}\n\n**Your Portfolio Volatility:**\n- Last Year: **${vol1y.Native}%** (SPY: ${vol1y.SPY}%)\n- Last 3 Years: **${vol3y.Native}%** (SPY: ${vol3y.SPY}%)\n\n${interpretation}\n\n${knowledge.relevance}`, {
                explanationWhat: knowledge.what,
                vol1yNative: vol1y.Native,
                vol1ySpy: vol1y.SPY,
                vol3yNative: vol3y.Native,
                vol3ySpy: vol3y.SPY,
                interpretation: interpretation,
                relevance: knowledge.relevance
            });
        }
    }

    // Strategy Questions - Risk Profile
    if (q.includes('risk profile') || q.includes('should i switch') || q.includes('change profile')) {
        const riskDescriptions = {
            'Averse': 'focuses on capital preservation with lower volatility and more conservative positions',
            'Neutral': 'balances growth and preservation with moderate risk-taking',
            'Seeker': 'maximizes growth potential with higher leverage and more aggressive positions'
        };
        
        const guidanceKey = risk === 'Averse' ? 'guidanceHigher' : risk === 'Seeker' ? 'guidanceLower' : 'guidanceBalanced';
        const guidanceText = tr(`riskProfile.${guidanceKey}`, risk === 'Averse' ? 'Neutral or Seeker profiles show higher growth potential with increased risk.' : risk === 'Seeker' ? 'Averse or Neutral profiles show more stability with potentially lower returns.' : 'Averse profile shows more safety, while Seeker profile shows higher growth potential.');
        return tr('riskProfile.base', `The current risk profile is **${riskLabel}**, which ${riskDescriptions[risk] || 'provides a balanced approach'}. Each profile represents different risk-return characteristics. ${guidanceText}`, {
            risk: riskLabel,
            description: riskDescriptions[risk] || 'provides a balanced approach',
            guidanceText
        });
    }

    // Strategy Questions - Market Conditions (Enhanced)
    if (q.includes('market') || q.includes('conditions') || q.includes('environment')) {
        const status = marketStatus.isOpen ? 'open' : 'closed';
        const spyQuote = quotes.find(q => q.ticker === 'SPY');
        const spyChange = spyQuote ? parseFloat(spyQuote.change) : 0;
        const marketKnowledge = getFinancialKnowledge('market');
        const statusLocalized = tr(`market.status.${status}`, status);
        
        const spyChangeFormatted = spyQuote ? `${spyChange >= 0 ? '+' : ''}${spyChange}` : null;
        const vix = marketContext?.vix?.current;
        const vixInterpretation = vix ? getInterpretation('vix', vix) : null;

        const base = tr('market.base', `${marketKnowledge?.what || 'The stock market is where shares are bought and sold.'}\n\nThe market is currently **${statusLocalized}** (${marketStatus.statusLabel}).`, {
            explanation: marketKnowledge?.what || '',
            status: statusLocalized,
            statusLabel: marketStatus.statusLabel
        });

        const spySegment = spyQuote
            ? tr('market.spySegment', `SPY is trading at **$${spyQuote.price}** (${spyChangeFormatted}%).`, {
                price: spyQuote.price,
                change: spyChangeFormatted
            })
            : '';

        const vixSegment = vix && vixInterpretation
            ? tr('market.vixSegment', `Current VIX: **${vix}** (${vixInterpretation}).`, {
                vix,
                interpretation: vixInterpretation
            })
            : '';

        const tail = tr('market.tail', `This portfolio setup reflects the ${riskLabel} risk profile, which adapts to current market conditions through dynamic allocation adjustments.`, { risk: riskLabel });

        return `${base} ${spySegment} ${vixSegment} ${tail}`.trim();
    }

    // Strategy Questions - Comparison
    if (q.includes('compare') || q.includes('vs') || q.includes('versus') || q.includes('better than')) {
        const performanceComparison = relativePerformanceData[risk]?.['Last 3 Years']?.Native > relativePerformanceData[risk]?.['Last 3 Years']?.SPY ? 'higher' : 'different';
        const performanceComparisonText = tr(`comparison.performance.${performanceComparison}`, performanceComparison === 'higher' ? 'higher returns' : 'different performance characteristics');
        return tr('comparison.base', `The ${risk} portfolio setup uses algorithmic rebalancing to optimize risk and return. Unlike buy-and-hold strategies, it dynamically adjusts allocation based on market conditions, exposure levels, and volatility. This approach shows ${performanceComparisonText} compared to passive index investing.`, {
            risk,
            performanceComparisonText
        });
    }

    // General Help
    if (q.includes('help') || q.includes('what can') || q.includes('what questions')) {
        return tr('help', `I can help you understand both **financial concepts** and **your dashboard data**!

**Financial Concepts:**
- What is VIX? / What is volatility? / What is beta?
- How does leverage work? / What are leveraged ETFs?
- Explain Sharpe ratio / correlation / returns
- What is SPY? / What is TQQQ? / What are ETFs?

**Dashboard-Specific:**
- Portfolio allocation (SPY, TQQQ, cash percentages)
- Exposure in time (how exposure changes over time)
- Performance metrics (3M, 1Y, 3Y returns)
- Risk metrics (beta, volatility)
- Market conditions and analytical insights
- Risk profile comparisons and characteristics

Try asking: "What is VIX?" or "Explain volatility" for educational content, or "What's my current allocation?" for dashboard-specific data.`);
    }

    // Fallback
    return tr('fallback', `I can help you understand financial concepts and dashboard metrics. Try asking:
- **Financial concepts**: "What is VIX?", "Explain volatility", "How does leverage work?"
- **Dashboard data**: "What's my allocation?", "How has performance been?", "What's my beta?"

You can also ask "What questions can I ask?" for more suggestions.`);
}

/**
 * Explain a financial concept with context from dashboard data
 * @param {string} concept - Concept name from financialKnowledge
 * @param {string} question - Original question
 * @param {object} dashboardData - Dashboard data for context
 * @returns {string} - Explanation response
 */
function explainFinancialConcept(concept, question, dashboardData) {
    const { risk, correlationData, volatilityData, marketContext, currentAllocation, exposureTimelineData } = dashboardData;
    
    // Get translated knowledge for the current language
    const knowledge = getFinancialKnowledge(concept);
    if (!knowledge) {
        return i18n.t('assistantResponses.fallback', { defaultValue: `I can explain ${concept}, but I need more context. Could you rephrase your question?` });
    }
    
    switch (concept) {
        case 'vix':
            const vix = marketContext?.vix?.current || 0;
            const vixInterpretation = getInterpretation('vix', vix);
            return `${knowledge.what}\n\n**Current VIX:** ${vix}\n${vixInterpretation}\n\n${knowledge.relevance}\n\n${knowledge.historical}`;

        case 'beta':
            const beta = parseFloat(correlationData?.m || "0");
            const betaInterpretation = getInterpretation('beta', beta);
            return `${knowledge.what}\n\n**Calculation:** ${knowledge.calculation}\n\n**Your Portfolio's Beta:** ${beta.toFixed(2)}\n${betaInterpretation}\n\n${knowledge.relevance}`;

        case 'volatility':
            const vol3y = volatilityData[risk]?.['Last 3 Years']?.Native || 0;
            const volInterpretation = getInterpretation('volatility', vol3y);
            return `${knowledge.what}\n\n**Calculation:** ${knowledge.calculation}\n\n**Your Portfolio's 3-Year Volatility:** ${vol3y}%\n${volInterpretation}\n\n${knowledge.relevance}\n\n${knowledge.relationship}`;

        case 'sharpe':
            return `${knowledge.what}\n\n**Calculation:** ${knowledge.calculation}\n\n${knowledge.relevance}`;

        case 'correlation':
            const betaForCorr = parseFloat(correlationData?.m || "0");
            const corrInterpretation = getInterpretation('correlation', betaForCorr);
            return `${knowledge.what}\n\n**Your Portfolio's Correlation with SPY:** ${betaForCorr.toFixed(2)}\n${corrInterpretation}\n\n${knowledge.relevance}`;

        case 'spy':
            const spyPercent = currentAllocation.find(a => a.ticker === 'SPY' || a.ticker === 'SPY or VOO')?.percent || 0;
            return `${knowledge.what}\n\n${knowledge.characteristics}\n\n**Your Portfolio:** ${spyPercent}% allocated to SPY\n${knowledge.relevance}\n\n${knowledge.performance}`;

        case 'tqqq':
            const tqqqPercent = currentAllocation.find(a => a.ticker === 'TQQQ')?.percent || 0;
            return `${knowledge.what}\n\n${knowledge.mechanics}\n\n**Risks:** ${knowledge.risks}\n\n**Your Portfolio:** ${tqqqPercent}% allocated to TQQQ\n${knowledge.relevance}\n\n**⚠️ Warning:** ${knowledge.warning}`;

        case 'upro':
            const uproPercent = currentAllocation.find(a => a.ticker === 'UPRO')?.percent || 0;
            return `${knowledge.what}\n\n${knowledge.mechanics}\n\n**Difference from TQQQ:** ${knowledge.difference}\n\n**Risks:** ${knowledge.risks}\n\n**Your Portfolio:** ${uproPercent}% allocated to UPRO\n${knowledge.relevance}\n\n**⚠️ Warning:** ${knowledge.warning}`;

        case 'treasuryBills':
            const cashPercent = currentAllocation.find(a => a.ticker === 'Cash' || a.ticker === 'T-bill or cash')?.percent || 0;
            return `${knowledge.what}\n\n**Types:** ${knowledge.types}\n\n**Safety:** ${knowledge.safety}\n\n**Returns:** ${knowledge.returns}\n\n**Your Portfolio:** ${cashPercent}% allocated to risk-free assets (T-bills/cash)\n${knowledge.relevance}\n\n**Role in Your Portfolio:** ${knowledge.role}`;

        case 'qqq':
            return `${knowledge.what}\n\n${knowledge.characteristics}\n\n${knowledge.difference}`;

        case 'leverage':
            const leveragePercent = currentAllocation.find(a => a.ticker === 'TQQQ')?.percent || 0;
            return `${knowledge.what}\n\n${knowledge.how}\n\n**Risks:** ${knowledge.risks}\n\n**Your Portfolio:** Currently using ${leveragePercent}% leverage through TQQQ\n${knowledge.relevance}`;

        case 'returns':
            const perf3y = dashboardData.relativePerformanceData?.[risk]?.['Last 3 Years']?.Native || 0;
            return `${knowledge.what}\n\n**Calculation Methods:**\n${knowledge.calculation}\n\n**Types:** ${knowledge.types}\n\n**Your Portfolio:** 3-Year Return: **${perf3y}%**\n${knowledge.relevance}\n\n**Note:** ${knowledge.compounding}`;

        case 'momentum':
            const momentum = marketContext?.market_indicators?.momentum || 0;
            return `${knowledge.what}\n\n${knowledge.interpretation}\n\n**Current Momentum Indicator:** ${momentum.toFixed(3)}\n${knowledge.relevance}`;

        case 'riskAversion':
            const riskAversion = marketContext?.market_indicators?.risk_aversion || 0;
            return `${knowledge.what}\n\n${knowledge.interpretation}\n\n**Current Risk Aversion Indicator:** ${riskAversion.toFixed(3)}\n${knowledge.relevance}`;

        case 'marketDirection':
            const marketDir = marketContext?.market_indicators?.market_direction || 0;
            return `${knowledge.what}\n\n${knowledge.interpretation}\n\n**Current Market Direction:** ${marketDir.toFixed(3)} (annualized)\n${knowledge.relevance}`;

        case 'exposure':
            const currentExposure = exposureTimelineData?.current?.fullExposure || 0;
            return `${knowledge.what}\n\n${knowledge.calculation}\n\n**Your Current Exposure:** ${currentExposure.toFixed(1)}%\n${knowledge.relevance}\n\n${knowledge.interpretation}`;

        case 'allocation':
            const spyAlloc = currentAllocation.find(a => a.ticker === 'SPY' || a.ticker === 'SPY or VOO')?.percent || 0;
            const tqqqAlloc = currentAllocation.find(a => a.ticker === 'TQQQ')?.percent || 0;
            const cashAlloc = currentAllocation.find(a => a.ticker === 'Cash' || a.ticker === 'T-bill or cash')?.percent || 0;
            return `${knowledge.what}\n\n${knowledge.relevance}\n\n**Your Current Allocation:**\n- SPY: ${spyAlloc}%\n- TQQQ: ${tqqqAlloc}%\n- Cash: ${cashAlloc}%\n\n${knowledge.components}`;

        case 'riskProfile':
            return `${knowledge.what}\n\n**Profiles:**\n- **Averse:** ${knowledge.averse}\n- **Neutral:** ${knowledge.neutral}\n- **Seeker:** ${knowledge.seeker}\n\n**Your Current Profile:** ${risk}\n${knowledge.relevance}`;

        case 'market':
            const status = dashboardData.marketStatus?.isOpen ? 'open' : 'closed';
            return `${knowledge.what}\n\n${knowledge.hours}\n\n**Factors Influencing Markets:**\n${knowledge.factors}\n\n**Current Status:** ${status}\n${knowledge.relevance}`;

        case 'volatilityDecay':
            return `${knowledge.what}\n\n${knowledge.mechanics}\n\n${knowledge.relevance}`;

        default:
            return i18n.t('assistantResponses.fallback', { defaultValue: `I can explain ${concept}, but I need more context. Could you rephrase your question?` });
    }
}

/**
 * Get suggested questions based on dashboard state
 */
export function getSuggestedQuestions(dashboardData) {
    const { risk, exposureTimelineData, currentAllocation } = dashboardData;
    const leveragePercent = currentAllocation.find(a => a.ticker === 'TQQQ')?.percent || 0;
    const currentExposure = exposureTimelineData?.current?.fullExposure || 0;
    const lang = (i18n.language || 'en').startsWith('es') ? 'es' : 'en';
    
    // Educational questions about Altradenative and financial concepts
    const educationalQuestions = lang === 'es'
        ? [
            "¿Qué es Altradenative?",
            "¿Cómo funciona Altradenative?",
            "¿Cuál es el performance pasado?",
            "¿Qué es el VIX?",
            "¿Qué es el beta y cómo funciona?",
            "¿Cómo se calcula la volatilidad?",
            "¿Cuál es la diferencia entre SPY y TQQQ?",
            "¿Cómo funciona el apalancamiento en los ETFs?",
            "¿Qué es el ratio de Sharpe?",
            "Explica la correlación y la diversificación"
        ]
        : [
            "What is Altradenative?",
            "How does Altradenative work?",
            "What is past performance?",
            "What is VIX?",
            "What is beta and how does it work?",
            "How is volatility calculated?",
            "What's the difference between SPY and TQQQ?",
            "How does leverage work in ETFs?",
            "What is the Sharpe ratio?",
            "Explain correlation and diversification"
        ];
    
    // Dashboard-specific questions
    const dashboardQuestions = lang === 'es'
        ? [
            "¿Qué significa mi asignación actual?",
            `¿Por qué mi apalancamiento es ${leveragePercent}%?`,
            "¿Cómo se ha comportado mi portafolio recientemente?",
            "¿Cuál es el beta de mi portafolio?",
            "¿Cuál es la volatilidad del mercado ahora mismo?",
            "¿Debería cambiar mi perfil de riesgo?",
            "¿Cómo ha cambiado la exposición con el tiempo?"
        ]
        : [
            "What does my current allocation mean?",
            `Why is my leverage ${leveragePercent}%?`,
            "How has my portfolio performed recently?",
            "What's my portfolio's beta?",
            "What's the market volatility right now?",
            "Should I change my risk profile?",
            "How has exposure changed over time?"
        ];

    // Combine and shuffle for variety
    const allQuestions = [...educationalQuestions.slice(0, 3), ...dashboardQuestions.slice(0, 4)];

    // Add context-specific questions
    if (currentExposure > 80) {
        allQuestions.push(lang === 'es' ? "¿Por qué la exposición es tan alta?" : "Why is exposure so high?");
    } else if (currentExposure < 30) {
        allQuestions.push(lang === 'es' ? "¿Por qué la exposición es tan baja?" : "Why is exposure so low?");
    }

    if (leveragePercent === 0) {
        allQuestions.push(lang === 'es' ? "¿Por qué el apalancamiento está en 0%?" : "Why is leverage at 0%?");
    }

    // Shuffle array to mix educational and dashboard questions
    for (let i = allQuestions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [allQuestions[i], allQuestions[j]] = [allQuestions[j], allQuestions[i]];
    }

    return allQuestions.slice(0, 7); // Return top 7 questions
}

